import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { courtApi } from '../services/api';
import { useLiff } from '../hooks/useLiff';
import BuyCreditSheet from './BuyCreditSheet';
import GiftIcon from './icons/GiftIcon';
import { sportGlyph } from '../utils/sportGlyph';

const PAGE_SIZE = 10;

const UserProfile = ({ user }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { liff } = useLiff();
  const [showModal, setShowModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [courtTypes, setCourtTypes] = useState([]);

  // --- Booking history pagination (page 1 arrives with the profile) ---
  const [bookings, setBookings] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  // --- Self-cancel (F1): free full-refund quota per month ---
  const [freeCancelsRemaining, setFreeCancelsRemaining] = useState(0);
  const [tokenOverrides, setTokenOverrides] = useState({}); // reflect refunds live
  const [confirmCancel, setConfirmCancel] = useState(null); // booking pending confirmation
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // --- Reopen payment for a pending booking (fresh ChillPay QR each time) ---
  const [payingId, setPayingId] = useState(null);
  const [payError, setPayError] = useState(null); // { id, message }

  // --- Buy credit sheet (F2) ---
  const [buySheet, setBuySheet] = useState({ open: false, courtTypeId: '' });
  const openBuy = (courtTypeId = '') => setBuySheet({ open: true, courtTypeId });

  // Seed / reset the list from the profile's first page whenever the user changes.
  useEffect(() => {
    setBookings(user?.bookings || []);
    setCursor(user?.bookingsCursor || null);
    setHasMore(!!user?.bookingsHasMore);
    setLoadError(false);
    setFreeCancelsRemaining(user?.freeCancelsRemaining ?? 0);
    setTokenOverrides({});
  }, [user]);

  // A booking is self-cancellable if it's future, confirmed, and token-paid.
  const bkkTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
  const bkkNowHHMM = () => new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit', hour12: false });
  const isFuture = (b) => b.date > bkkTodayStr() || (b.date === bkkTodayStr() && (b.startTime || '') > bkkNowHHMM());
  // Any confirmed (paid) future booking is self-cancellable; refund is the
  // token-equivalent of the booked time (1 token = 1 hour), token- OR cash-paid.
  const refundTokensOf = (b) => (Number(b.duration) || 0) / 60;
  const fmtTok = (n) => (n % 1 === 0 ? String(n) : n.toFixed(1));

  // A pending QR booking is payable until its hold ends (server-computed).
  const isPayable = (b) => b.status === 'pending' && b.paymentStatus === 'pending'
    && !!b.holdExpiresAt && Date.now() < new Date(b.holdExpiresAt).getTime();
  const isPaymentExpired = (b) => b.status === 'pending' && b.paymentStatus === 'pending' && !isPayable(b);
  const fmtClock = (iso) => new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit', hour12: false });

  const openPayment = async (booking) => {
    if (payingId || !user?.idToken) return;
    setPayingId(booking.id);
    setPayError(null);
    try {
      const res = await courtApi.getPaymentLink(user.idToken, booking.id, liff);
      if (res?.status === 'paid' || res?.status === 'refund_due') {
        // refund_due = an old QR was paid but couldn't confirm; the success page explains it
        handleClose();
        const to = res.status === 'paid' ? `/receipt/${res.paymentId}` : `/payment/success/${res.paymentId}`;
        setTimeout(() => navigate(to), 100);
      } else if (res?.paymentUrl) {
        window.location.href = res.paymentUrl;
        return; // keep the spinner while the browser navigates away
      }
    } catch (err) {
      if (err.code === 'EXPIRED' || err.code === 'NOT_PENDING') {
        // Flip the row to "expired" locally
        setBookings((prev) => prev.map((b) => (b.id === booking.id ? { ...b, holdExpiresAt: new Date().toISOString() } : b)));
      }
      const message = err.code === 'EXPIRED' ? t('payment_expired')
        : err.code === 'LINK_LIMIT' ? t('payment_link_limit')
          : t('payment_link_error');
      setPayError({ id: booking.id, message });
    }
    setPayingId(null);
  };

  const doCancel = async () => {
    if (!confirmCancel || !user?.idToken) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await courtApi.selfCancelBooking(user.idToken, confirmCancel.id, liff);
      setBookings((prev) => prev.map((b) => (b.id === confirmCancel.id
        ? { ...b, status: 'cancelled', paymentStatus: 'cancelled' } : b)));
      setFreeCancelsRemaining(res.remainingFreeCancels);
      if (res.courtTypeId != null) setTokenOverrides((prev) => ({ ...prev, [res.courtTypeId]: res.newBalance }));
      setConfirmCancel(null);
    } catch (err) {
      setCancelError(err.message || (t('cancel_failed') || 'Failed to cancel'));
    } finally {
      setCancelling(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursor || !user?.idToken) return;
    setLoadingMore(true);
    setLoadError(false);
    try {
      const page = await courtApi.getUserBookings(
        user.idToken,
        { cursor, limit: PAGE_SIZE },
        liff
      );
      setBookings((prev) => [...prev, ...page.bookings]);
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (err) {
      console.error('Failed to load more bookings:', err);
      setLoadError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, cursor, user?.idToken, liff]);

  // Auto-load when the sentinel scrolls into view (root = the modal scroll box).
  // The "Load more" button is the guaranteed fallback if LIFF drops scroll events.
  useEffect(() => {
    if (!showModal || !hasMore) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollRef.current || null, rootMargin: '120px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showModal, hasMore, loadMore]);

  useEffect(() => {
    fetchCourtTypes();
  }, []);

  const fetchCourtTypes = async () => {
    try {
      const data = await courtApi.getCourtTypes();
      setCourtTypes(data);
    } catch (err) {
      console.error('Error fetching court types:', err);
    }
  };

  const getCourtTypeName = (courtTypeId) => {
    const courtType = courtTypes.find(ct => ct.id === courtTypeId);
    return courtType ? courtType.name : courtTypeId; // Fallback to ID if not found
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowModal(false);
      setIsClosing(false);
    }, 300);
  };

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (time) => {
    if (!time) return '';
    return time;
  };

  // Helper function to get booking status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600';
      case 'cancelled':
        return 'text-red-600';
      case 'pending':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  // Helper function to get payment status color
  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Handle booking click
  const handleBookingClick = (booking) => {
    if (booking.paymentStatus === 'paid' && booking.payment?.id) {
      // Navigate to receipt if payment is completed
      handleClose();
      setTimeout(() => {
        navigate(`/receipt/${booking.payment.id}`);
      }, 100);
    } else if (isPayable(booking)) {
      // ChillPay links are single-use, so get a fresh QR rather than the stored URL
      openPayment(booking);
    }
  };

  return (
    <>
      {/* Identity block → opens the profile modal (hours + history + cancel) */}
      <button
        onClick={() => setShowModal(true)}
        className="shrink-0 flex items-center gap-2.5 text-left group"
      >
        <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center shrink-0">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.displayName}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xl font-medium text-gray-600">
              {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-base font-bold text-gray-900 truncate max-w-[7rem] sm:max-w-[10rem]">
            {user?.displayName}
          </p>
          <span className="flex items-center gap-0.5 text-xs font-medium text-indigo-600 group-hover:text-indigo-700 min-w-0">
            {/* ประวัติ and ยกเลิกการจอง on separate lines for a clearer statement */}
            <span className="flex flex-col leading-tight min-w-0">
              <span className="truncate">{t('history') || 'History'}</span>
              <span className="truncate">{t('cancel_booking') || 'Cancel Booking'}</span>
            </span>
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </button>

      {/* Modal (portaled to body so it escapes the fixed header pill) */}
      {showModal && createPortal((
        <div
          onClick={handleClose}
          className={`fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-end sm:items-center justify-center sm:p-4 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
        >
          <div
            ref={scrollRef}
            onClick={(e) => e.stopPropagation()}
            className={`relative bg-white rounded-t-3xl sm:rounded-3xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 ease-out sm:scale-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`}
          >
            {/* Header (stays pinned while the body scrolls) */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 rounded-t-3xl">
              <div className="flex justify-center pt-2.5 sm:hidden"><div className="w-12 h-1 bg-gray-300 rounded-full" /></div>
              <div className="flex items-center justify-between px-6 pt-3 pb-4">
                <h2 className="text-xl font-semibold text-gray-900">{t('user_profile') || 'Profile'}</h2>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* User Info */}
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-medium text-gray-600">
                      {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{user?.displayName}</h3>
                  <p className="text-sm text-gray-500">{user?.lineUserId}</p>
                </div>
              </div>

              {/* Hours balances — compact card grid, most hours first, tap a card
                  to top up that court type. */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-gray-700">{t('your_tokens') || 'Hours remaining'}</h5>
                  <button onClick={() => openBuy('')} className="flex items-center gap-1 text-sm font-semibold text-fuchsia-600 hover:text-fuchsia-700">
                    <GiftIcon className="w-4 h-4" /> {t('promotion') || 'Promotion'}
                  </button>
                </div>
                {(() => {
                  const displayTokens = { ...(user?.tokens || {}), ...tokenOverrides };
                  const entries = Object.entries(displayTokens)
                    .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0));
                  return entries.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {entries.map(([courtTypeId, tokenAmount]) => {
                        const name = getCourtTypeName(courtTypeId);
                        return (
                          <button
                            key={courtTypeId}
                            onClick={() => openBuy(courtTypeId)}
                            title={t('promotion')}
                            className="relative flex flex-col gap-1 p-3 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-blue-100 hover:border-indigo-300 hover:shadow-sm active:scale-95 transition text-left"
                          >
                            <span className="flex items-center gap-1.5 min-w-0">
                              <span className="text-base leading-none">{sportGlyph(name)}</span>
                              <span className="truncate text-xs font-medium text-gray-500 capitalize">{name}</span>
                            </span>
                            <span className="flex items-baseline gap-1">
                              <span className="text-xl font-bold text-indigo-700 leading-none">{Number(tokenAmount).toFixed(1)}</span>
                              <span className="text-xs text-gray-400">ชม.</span>
                            </span>
                            <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/80 text-indigo-400 flex items-center justify-center text-sm font-bold leading-none">+</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-center py-6 text-gray-500 text-sm rounded-2xl bg-gray-50">
                      {t('no_tokens_available') || 'No hours yet'}
                      <button onClick={() => openBuy('')} className="inline-flex items-center gap-1 text-fuchsia-600 font-semibold"><GiftIcon className="w-4 h-4" /> {t('promotion')}</button>
                    </div>
                  );
                })()}
              </div>

              {/* Booking History Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">{t('booking_history') || 'Booking History'}</h4>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${freeCancelsRemaining > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    🎟 {t('free_cancels_left')} {freeCancelsRemaining}/{user?.freeCancelQuota ?? 2} {t('this_month')}
                  </span>
                </div>
                {bookings.length > 0 ? (
                  <div className="space-y-3">
                    {bookings.map((booking) => {
                      const cancellable = booking.status === 'confirmed' && isFuture(booking);
                      return (
                      <div
                        key={booking.id}
                        onClick={() => handleBookingClick(booking)}
                        className="p-4 rounded-2xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <h5 className="font-semibold text-gray-900 leading-snug">{booking.courtName}</h5>
                          <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                            isPaymentExpired(booking) ? 'bg-gray-100 text-gray-500' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {isPaymentExpired(booking) ? t('payment_expired') : (t(booking.status) || booking.status)}
                          </span>
                        </div>

                        <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-700">
                          <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{formatDate(booking.date)}</span>
                          <span className="text-gray-300">·</span>
                          <span>{formatTime(booking.startTime)}–{formatTime(booking.endTime)}</span>
                        </div>

                        <div className="mt-1 text-sm">
                          <span className={isPaymentExpired(booking) ? 'text-gray-500' : getPaymentStatusColor(booking.paymentStatus)}>
                            {t('payment')}: {isPaymentExpired(booking) ? t('payment_expired') : (t(booking.paymentStatus) || booking.paymentStatus)}
                          </span>
                          {booking.payment?.paymentMethod === 'tokens' && (
                            <span className="text-blue-600"> · {t('used')} {booking.payment.tokensUsed} {t('tokens')}</span>
                          )}
                        </div>

                        {/* A QR payment that couldn't confirm this booking: staff refund it by hand */}
                        {booking.refund && (
                          booking.refund.status === 'refunded' ? (
                            <div className="mt-2 rounded-xl bg-green-50 px-3 py-2 text-xs text-green-700">
                              ✓ {t('refund_status_refunded')} ฿{Number(booking.refund.amount).toLocaleString()}
                            </div>
                          ) : (
                            <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                              <p className="font-semibold">⚠️ {t(`refund_title_${booking.refund.reason}`)}</p>
                              <p className="mt-0.5">{t('refund_amount')} ฿{Number(booking.refund.amount).toLocaleString()} · {t('refund_status_due')}</p>
                              <p className="mt-0.5 text-amber-700/80">{t('refund_ref')}: {(booking.refund.payments || []).map((p) => p.paymentId).join(', ')}</p>
                            </div>
                          )
                        )}

                        {/* Reopen payment: each tap gets a fresh QR (ChillPay links are single-use) */}
                        {isPayable(booking) && (
                          <div className="mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-xs text-gray-500">⏳ {t('pay_before')} {fmtClock(booking.holdExpiresAt)}</span>
                              <button
                                onClick={() => openPayment(booking)}
                                disabled={!!payingId}
                                className="shrink-0 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 active:scale-95 transition disabled:opacity-60"
                              >
                                {payingId === booking.id ? t('opening_payment') : t('pay_now')}
                              </button>
                            </div>
                            {payError?.id === booking.id && (
                              <p className="mt-2 text-xs text-red-600">{payError.message}</p>
                            )}
                          </div>
                        )}
                        {!isPayable(booking) && payError?.id === booking.id && (
                          <p className="mt-2 text-xs text-red-600">{payError.message}</p>
                        )}

                        {/* Self-cancel action (F1) */}
                        {cancellable && (
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3" onClick={(e) => e.stopPropagation()}>
                            {freeCancelsRemaining > 0 ? (
                              <>
                                <span className="text-xs text-gray-500">🎟 {t('you_will_get_back')} {fmtTok(refundTokensOf(booking))} {t('tokens')}</span>
                                <button
                                  onClick={() => { setCancelError(null); setConfirmCancel(booking); }}
                                  className="shrink-0 px-4 py-2 rounded-xl border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 active:scale-95 transition"
                                >
                                  {t('cancel_booking')}
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-amber-600 ml-auto font-medium">⚠️ {t('cancel_quota_used')}</span>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}

                    {/* Infinite-scroll sentinel (auto-loads next page when visible) */}
                    <div ref={sentinelRef} aria-hidden="true" className="h-px" />

                    {loadingMore && (
                      <div className="flex justify-center py-3">
                        <svg className="w-5 h-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      </div>
                    )}

                    {loadError && (
                      <button
                        onClick={loadMore}
                        className="w-full py-2 text-sm text-red-600 hover:text-red-700"
                      >
                        {t('load_failed_retry') || 'Failed to load — tap to retry'}
                      </button>
                    )}

                    {hasMore && !loadingMore && !loadError && (
                      <button
                        onClick={loadMore}
                        className="w-full py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-100 rounded-lg"
                      >
                        {t('load_more') || 'Load more'}
                      </button>
                    )}

                    {!hasMore && bookings.length > PAGE_SIZE && (
                      <p className="text-sm text-gray-400 text-center mt-2">
                        {t('all_bookings_shown') || 'All bookings shown'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p>{t('no_bookings_yet') || 'No bookings found'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Self-cancel confirmation (F1) */}
      {confirmCancel && createPortal((
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={() => !cancelling && setConfirmCancel(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('cancel_this_booking')}</h3>
            <p className="text-sm text-gray-600 mb-2">
              {confirmCancel.courtName} · {formatDate(confirmCancel.date)} {formatTime(confirmCancel.startTime)}
            </p>
            <p className="text-sm text-green-700 mb-4">
              🎟 {t('you_will_get_back')} {fmtTok(refundTokensOf(confirmCancel))} {t('tokens')} · {t('free_cancels_left')} {freeCancelsRemaining}/{user?.freeCancelQuota ?? 2}
            </p>
            {cancelError && <p className="text-sm text-red-600 mb-3">{cancelError}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmCancel(null)} disabled={cancelling} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                {t('keep_booking')}
              </button>
              <button onClick={doCancel} disabled={cancelling} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                {cancelling ? '…' : t('confirm_cancel_btn')}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      <BuyCreditSheet
        open={buySheet.open}
        defaultCourtTypeId={buySheet.courtTypeId}
        idToken={user?.idToken}
        liff={liff}
        userTokens={{ ...(user?.tokens || {}), ...tokenOverrides }}
        onClose={() => setBuySheet({ open: false, courtTypeId: '' })}
      />
    </>
  );
};

export default UserProfile; 